import {
  type AuthUser,
  hasAnyPermission,
  hasPermission,
  type MeResponse,
  type Permission,
  type TokenResponse,
} from '@field-support/shared';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, onSessionExpired, refreshSession, setAccessToken } from '@/shared/api';

type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  login: (phoneNumber: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** True when the user holds EVERY given permission. */
  can: (...permissions: Permission[]) => boolean;
  /** True when the user holds ANY of the given permissions. */
  canAny: (...permissions: Permission[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/** How often role/permission changes made by other admins are picked up. */
const PERMISSION_REFRESH_MS = 5 * 60 * 1000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);

  const signOutLocally = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setStatus('anonymous');
    // Cached data belongs to the ended session; the next user may not share its permissions.
    queryClient.clear();
  }, [queryClient]);

  // Restore a session from the httpOnly refresh cookie on load.
  useEffect(() => {
    let cancelled = false;
    void refreshSession().then((session) => {
      if (cancelled) return;
      if (session) {
        setUser(session.user);
        setStatus('authenticated');
      } else {
        setStatus('anonymous');
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => onSessionExpired(signOutLocally), [signOutLocally]);

  // Poll /auth/me so permission changes apply without a re-login.
  useEffect(() => {
    if (status !== 'authenticated') return;
    const timer = setInterval(() => {
      api<MeResponse>('/auth/me')
        .then((me) => setUser(me.user))
        .catch(() => undefined);
    }, PERMISSION_REFRESH_MS);
    return () => clearInterval(timer);
  }, [status]);

  const login = useCallback(async (phoneNumber: string, password: string) => {
    const session = await api<TokenResponse>('/auth/login', {
      method: 'POST',
      body: { phoneNumber, password, client: 'web' },
    });
    setAccessToken(session.accessToken);
    setUser(session.user);
    setStatus('authenticated');
  }, []);

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST', body: {} });
    } finally {
      signOutLocally();
    }
  }, [signOutLocally]);

  // `user.permissions` is the server-resolved effective set
  // (resolveEffectivePermissions over role defaults + overrides, applied server-side).
  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      login,
      logout,
      can: (...permissions) => Boolean(user && hasPermission(user.permissions, ...permissions)),
      canAny: (...permissions) => Boolean(user && hasAnyPermission(user.permissions, ...permissions)),
    }),
    [status, user, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
